import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconLoader2,
} from '@tabler/icons-react';
import { ButtonField, ButtonFieldIcon } from '@wk/components-react16';

import { fetchCheckpointMetadata } from '@/domains/checkpoint/checkpointStandardsApi';
import {
  getAccountableJiraBoards,
  upsertAccountableJiraBoards,
} from '@/domains/access/systemConfigApi';
import useToast from '@/shared/stores/toastStore';
import type { AccountableJiraBoards } from '@/types';

const normalizeBoards = (boards: AccountableJiraBoards): AccountableJiraBoards =>
  Object.fromEntries(
    Object.entries(boards)
      .map(([accountable, projectKey]) => [
        accountable.trim(),
        projectKey.trim(),
      ])
      .filter(([accountable, projectKey]) => accountable && projectKey),
  );

const stableBoardsKey = (boards: AccountableJiraBoards) =>
  JSON.stringify(
    Object.entries(normalizeBoards(boards)).sort(([left], [right]) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    ),
  );

const getCaseInsensitiveValue = (
  boards: AccountableJiraBoards,
  accountable: string,
) => {
  const direct = boards[accountable];
  if (direct !== undefined) return direct;

  const entry = Object.entries(boards).find(
    ([key]) => key.toLowerCase() === accountable.toLowerCase(),
  );
  return entry?.[1] ?? '';
};

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | string
    | { title?: string; message?: string }
    | undefined;
  if (typeof data === 'string') return data;
  return data?.title || data?.message || fallback;
};

interface JiraBoardRow {
  accountable: string;
  checkpointCount: number;
  jiraProjectKey: string;
}

const JiraBoardsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [draft, setDraft] = useState<AccountableJiraBoards>({});

  const boardsQ = useQuery({
    queryKey: ['system', 'jira-boards'],
    queryFn: async () => (await getAccountableJiraBoards()).data,
  });

  const metadataQ = useQuery({
    queryKey: ['hub', 'checkpoint-metadata'],
    queryFn: fetchCheckpointMetadata,
    retry: false,
  });

  useEffect(() => {
    if (boardsQ.data) {
      setDraft(boardsQ.data);
    }
  }, [boardsQ.data]);

  const accountableCounts = useMemo(() => {
    const counts = new Map<string, number>();

    for (const entry of Object.values(metadataQ.data ?? {})) {
      const accountable = entry.accountable?.trim();
      if (!accountable) continue;

      counts.set(accountable, (counts.get(accountable) ?? 0) + 1);
    }

    return counts;
  }, [metadataQ.data]);

  const rows = useMemo<JiraBoardRow[]>(() => {
    const accountables = new Map<string, string>();
    const addAccountable = (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      const key = trimmed.toLowerCase();
      if (!accountables.has(key)) {
        accountables.set(key, trimmed);
      }
    };

    for (const accountable of accountableCounts.keys()) {
      addAccountable(accountable);
    }
    for (const accountable of Object.keys(boardsQ.data ?? {})) {
      addAccountable(accountable);
    }

    return Array.from(accountables.values())
      .sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: 'base' }),
      )
      .map((accountable) => ({
        accountable,
        checkpointCount: accountableCounts.get(accountable) ?? 0,
        jiraProjectKey: getCaseInsensitiveValue(draft, accountable),
      }));
  }, [accountableCounts, boardsQ.data, draft]);

  const mappedCount = rows.filter((row) => row.jiraProjectKey.trim()).length;
  const hasChanges =
    stableBoardsKey(draft) !== stableBoardsKey(boardsQ.data ?? {});

  const saveMutation = useMutation({
    mutationFn: async () =>
      (await upsertAccountableJiraBoards(normalizeBoards(draft))).data,
    onSuccess: (saved) => {
      setDraft(saved);
      queryClient.setQueryData(['system', 'jira-boards'], saved);
      queryClient.invalidateQueries({ queryKey: ['system', 'jira-boards'] });
      showToast('success', {
        title: 'Jira boards saved',
        message: 'Accountable mapping updated.',
      });
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not save Jira boards',
        message: apiErrorMessage(error, 'Failed to update the mapping.'),
      });
    },
  });

  const updateDraft = useCallback((accountable: string, value: string) => {
    setDraft((current) => {
      const existingKey =
        Object.keys(current).find(
          (key) => key.toLowerCase() === accountable.toLowerCase(),
        ) ?? accountable;

      return {
        ...current,
        [existingKey]: value.toUpperCase(),
      };
    });
  }, []);

  const isLoading = boardsQ.isLoading;

  const columnDefs = useMemo(
    (): ColDef<JiraBoardRow>[] => [
      {
        headerName: 'Accountable',
        field: 'accountable',
        minWidth: 320,
        flex: 2,
        autoHeight: true,
        cellRenderer: (params: CustomCellRendererProps<JiraBoardRow>) => {
          const row = params.data;
          if (!row) return null;

          return (
            <div className="flex flex-col py-1 leading-tight">
              <span className="break-words font-medium text-gray-900">
                {row.accountable}
              </span>
              <span className="text-xs text-gray-500">
                {row.checkpointCount
                  ? `${row.checkpointCount} checkpoint(s)`
                  : 'Configured only'}
              </span>
            </div>
          );
        },
      },
      {
        headerName: 'Jira project key',
        field: 'jiraProjectKey',
        minWidth: 240,
        flex: 1,
        sortable: false,
        cellRenderer: (params: CustomCellRendererProps<JiraBoardRow>) => {
          const row = params.data;
          if (!row) return null;

          return (
            <div className="flex h-full items-center">
              <input
                type="text"
                value={row.jiraProjectKey}
                onChange={(event) =>
                  updateDraft(row.accountable, event.target.value)
                }
                aria-label={`Jira project key for ${row.accountable}`}
                autoComplete="off"
                className="h-8 w-full min-w-0 border border-input px-3 text-sm outline-none hover:border-[#005b92] focus:border-[#000]"
              />
            </div>
          );
        },
      },
      {
        headerName: 'Status',
        colId: 'status',
        width: 140,
        sortable: false,
        cellRenderer: (params: CustomCellRendererProps<JiraBoardRow>) => {
          const mapped = (params.data?.jiraProjectKey.trim().length ?? 0) > 0;

          return (
            <div className="flex h-full items-center">
              <span
                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                  mapped
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {mapped ? 'Mapped' : 'Unmapped'}
              </span>
            </div>
          );
        },
      },
    ],
    [updateDraft],
  );

  return (
    <div className="flex flex-col gap-4 px-4 lg:pr-8 py-4 h-full max-sm:h-auto max-sm:gap-3 max-sm:px-0 max-sm:py-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ButtonFieldIcon>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              aria-label="Back to settings"
            >
              <IconArrowLeft size={28} />
            </button>
          </ButtonFieldIcon>
          Jira Boards
        </h1>
        <ButtonField>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending || boardsQ.isLoading}
            className="disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="flex items-center gap-2">
              Save
              {saveMutation.isPending ? (
                <IconLoader2 size={16} className="animate-spin" />
              ) : null}
            </span>
          </button>
        </ButtonField>
      </div>

      <p className="text-sm text-gray-500 -mt-2">
        Map accountable checkpoint owners to Jira project keys.
      </p>

      <div className="text-sm text-gray-500">
        {mappedCount}/{rows.length} mapped
      </div>

      {metadataQ.isError ? (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <IconAlertTriangle size={18} className="shrink-0" />
          Hub metadata unavailable. Showing configured mappings.
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        {boardsQ.isError ? (
          <div className="rounded-md border bg-white p-4 text-sm text-red-600">
            Failed to load Jira board mappings.
          </div>
        ) : isLoading ? (
          <div className="flex min-h-[220px] items-center justify-center text-gray-500">
            <IconLoader2 size={22} className="animate-spin" />
          </div>
        ) : (
          <div className="cg-ag-grid-field ag-theme-balham mt-2 flex w-full flex-col gap-4 sm:flex-1 sm:min-h-0">
            <div className="w-full sm:flex-1 sm:min-h-0 max-sm:overflow-x-auto">
              <div className="h-full max-sm:h-[52vh] max-sm:min-h-[320px] max-sm:min-w-[860px] max-sm:w-full">
                <AgGridReact
                  animateRows={false}
                  loading={isLoading}
                  rowData={rows}
                  columnDefs={columnDefs}
                  getRowId={(params) => params.data.accountable}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JiraBoardsPage;
